import type { ProcedureStatus } from '@/types/cgt'

/**
 * Today as YYYY-MM-DD in the user's LOCAL calendar — deliberately not the UTC
 * `todayISO()` from scheduling/date.ts, which flips a day early west of
 * Greenwich. The overdue rule is a calendar-date comparison as the user sees
 * the calendar.
 */
export function localTodayISO(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * A procedure is overdue when its due date has PASSED (due today is not
 * overdue) and it is still in progress — completed procedures are done,
 * cancelled procedures are never overdue. Compares calendar-date strings,
 * never timestamps.
 */
export function isOverdue(
  dueDate: string | null | undefined,
  status: ProcedureStatus,
  today: string,
): boolean {
  if (!dueDate) return false
  if (status !== 'in_progress') return false
  return dueDate.slice(0, 10) < today
}

/**
 * `startedAt` as an absolute calendar date for the procedure lists' Started
 * column (z8nrz7dkzy).
 *
 * Replaces `timeAgo` there. `startedAt` is the case's creation timestamp and the
 * field the list is ordered by; rendering it as "26d ago" made staff do the
 * arithmetic back to a date to reconcile a case against an outside record, and
 * got it wrong.
 *
 * Locale is pinned to `en-US` rather than left to the browser. AC1.2 specifies
 * `M/D/YYYY` — the format `timeAgo` already fell back to past 30 days — and a
 * bare `toLocaleDateString()` would render `13/8/2026` for an en-GB user, which
 * is a different format, not a localised one. Pinning also makes this testable.
 * `src/lib/patient.ts` pins the same way.
 *
 * The calendar date is read in the RUNTIME's timezone, which is the closest
 * thing available to AC1.3's "tenant timezone" — there is no tenant timezone
 * setting in this codebase. A case started at 21:00 local on 8/13 therefore
 * reads `8/13/2026` rather than being pushed to the 14th by UTC.
 *
 * Unset renders an em-dash, matching the Scheduled and Due columns (AC1.5).
 * An unparseable timestamp does the same rather than rendering "Invalid Date".
 */
export function formatStartedOn(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US')
}
